import React, { useEffect, useState } from 'react';
import { Form, Schema, Stack, Col, Grid, Row, IconButton, Divider, FormInstance, Checkbox, SelectPicker} from 'rsuite';
import { useNavigate } from 'react-router-dom';
import { BaseErrorModel, BaseResponseModel, PageState } from '@/models/core.model';
import { ObjectStorageFileCategory } from '@/models/storage.model';
import PageContent from '@/components/PageContent';
import PageNextIcon from '@rsuite/icons/PageNext';
import PagePreviousIcon from '@rsuite/icons/PagePrevious';
import { AxiosError } from 'axios';
import ImageUploader from '@/components/ImageUploader';
import { MdFlag } from 'react-icons/md';
import { BrandGear, GearTypeList, UpsertBrandGear } from '@/models/brand-gear.model';
import { upsertBrandGear } from '@/services/brand-gear.service';
import FetchingSelect from '@/components/FetchingSelect';
import { getSportBrandsLookup } from '@/services/sport-brand.service';

const schema = Schema.Model<UpsertBrandGear>({
  id: Schema.Types.NumberType(),
  name: Schema.Types.StringType().isRequired('This field is required.'),
  coverImageUrl: Schema.Types.StringType(),
  sportBrandId: Schema.Types.NumberType().isRequired('This field is required.'),
  type: Schema.Types.NumberType().isRequired('This field is required.')
});


const emptyModel: UpsertBrandGear = {
  id: undefined,
  name: undefined,
  coverImageUrl: undefined,
  sportBrandId: undefined,
  type: undefined
};

interface BrandGearFormProps{
  formObject?: BrandGear;
  pageState: PageState
};

const BrandGearForm: React.FC<BrandGearFormProps> = ({formObject, pageState}) => {
  const [formValue, setFormValue] = useState<UpsertBrandGear>(emptyModel);
  const [formError, setFormError] = useState<Record<string, any>>({});
  const [errorModel, setErrorModel] = useState<BaseErrorModel | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  
  const nav = useNavigate();
  const formRef = React.useRef<FormInstance<Record<string, any>>>(null);

  useEffect(() => {
    if(pageState == PageState.New){
      setFormValue({...emptyModel, });
      return;
    }

    if (formObject) {
      const castedModel: UpsertBrandGear = { 
        ...formObject, 
      };

      setFormValue(castedModel);
    } else {
      setFormValue(emptyModel);
    }
  }, [formObject]);

  const handleSubmit = async () => {
    if (formRef.current && !formRef.current.check()) {
      console.log("errors:", formError);
      return;
    }
    setErrorModel(undefined);
    setLoading(true);
    try {
      const result: BaseResponseModel<any> = await upsertBrandGear(formValue);
      if (result.success) {
        nav('/brand-gears');
      }
    } catch (err) {
      setErrorModel((err as AxiosError<BaseErrorModel>).response?.data);
    }finally{
      setLoading(false);
    }
  };

  const handleFormChange = (value: Record<string, any>) => {
    setFormValue(prevFormValue => ({
      ...prevFormValue,
      ...value
    }));
  };

  return (
    <PageContent>
     <Form name='brand-gear-form' fluid
        ref={formRef}
        onChange={handleFormChange}
        onCheck={setFormError}
        formValue={formValue}
        model={schema}
        disabled={loading}
      >
        <Grid w='100%' fluid >
          <Row justifyContent="center">
            <Col span={{xs:24, xl:12}} >
              <Form.Group>
                <Form.Label>Sport Brand</Form.Label>
                <Form.Control name="sportBrandId" 
                  accepter={FetchingSelect} block
                  fetchData={(text, page) => getSportBrandsLookup(text, page)}
                  placeholder="Select a brand"
                />
              </Form.Group>                 
            </Col>
          </Row>
          <Row justifyContent="center">
            <Col span={{xs:24, xl:12}} >
              <Form.Group>
                <Form.Label>Name</Form.Label>
                <Form.Control name="name" value={formValue.name ?? ''}/>
              </Form.Group>
            </Col>
          </Row>                     
          <Row justifyContent="center">
            <Col span={{xs:24, xl:12}} >
              <Form.Group>
                <Form.Label>Gear Type</Form.Label>
                <Form.Control name="type" 
                  accepter={SelectPicker} block
                  data={GearTypeList}
                  placeholder="Select a type"
                />              
              </Form.Group>
            </Col> 
          </Row>
          <Row justifyContent="center">
            <Col span={{xs:24, xl:12}} >
              <Form.Group>
                <Form.Label>Cover Image</Form.Label>
                <Form.Control
                  accepter={ImageUploader}
                  name="coverImageUrl"
                  defaultIcon={MdFlag}
                  height="250px"
                  url={formValue.coverImageUrl}
                  objectStorageCategory={ObjectStorageFileCategory.BrandGear}
                  onChange={url => setFormValue(pre => ({...pre, coverImageUrl: url}))}              
                />
              </Form.Group>
            </Col>
          </Row>
        </Grid>
      </Form>

      <Divider />
      
      <Stack justifyContent="space-between">
        <IconButton icon={<PagePreviousIcon />} onClick={() => nav(-1)}>
          Back
        </IconButton>
        <IconButton
          icon={<PageNextIcon />}
          placement="right"
          appearance="primary"
          onClick={handleSubmit}
          loading={loading}
        >
          Save
        </IconButton>
      </Stack>
    </PageContent>
  );
};

export default BrandGearForm;

